/**
 * The product lines referential with its three dialogs (edit, ABC XY,
 * keyboards). Admin only.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ROLES } from '@/shared/constants';
import { appConfigQueryOptions } from '@/shared/config/app-config';
import { getUserProfile } from '@/features/auth/session';
import { hardNavigate } from '@/shared/lib/navigation';
import { TopMenu } from '@/shared/layout/TopMenu';
import { SortableHeader } from '@/shared/ui/SortableHeader';
import { useFillerRows } from '@/shared/ui/useFillerRows';
import plusIcon from '@/assets/images/plus.svg';
import { productLinesQueryOptions, type ProductLine } from './api';
import { ProductLineDialog } from './ProductLineDialog';
import { ProductLineXyDialog } from './ProductLineXyDialog';
import { KeyboardsDialog } from './KeyboardsDialog';
import {
  DEFAULT_SORT,
  keyboardLabel,
  nextSort,
  sortProductLines,
  type SortKey,
} from './logic';
import { kebabCase } from '@/shared/lib/kebab-case';

const COLUMNS: { key: SortKey; label: string; width: string }[] = [
  { key: 'productLine', label: 'Product line', width: '10%' },
  {
    key: 'commercialProductLine',
    label: 'Commercial product line',
    width: '15%',
  },
  { key: 'display', label: 'Display', width: '5%' },
  { key: 'productGroup', label: 'Product group', width: '10%' },
  { key: 'sav', label: 'Personalization post purchase allow', width: '20%' },
  { key: 'keyboardLabel', label: 'Keyboards', width: '15%' },
];
const WIDTHS = [10, 15, 5, 10, 20, 15, 15];

type DialogState =
  | { kind: 'edit'; item: ProductLine | null }
  | { kind: 'xy'; item: ProductLine }
  | { kind: 'keyboards'; item: ProductLine };

export function ProductLinesPage() {
  const user = getUserProfile();
  const isAdmin = user?.role === ROLES.ADMIN;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (user && !isAdmin) hardNavigate('/main');
  }, [user, isAdmin]);

  const productLines = useQuery({
    ...productLinesQueryOptions(),
    enabled: isAdmin,
  });
  const { data: config } = useQuery(appConfigQueryOptions());
  const stores = useMemo(() => config?.stores ?? [], [config?.stores]);
  const languages = useMemo(() => config?.languages ?? [], [config?.languages]);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const items = useMemo(
    () => sortProductLines(productLines.data ?? [], sort),
    [productLines.data, sort],
  );
  const fillerRows = useFillerRows(
    containerRef,
    'tr.product-line-row',
    items.length,
  );

  const closeDialog = (result: 'Confirm' | 'Cancel') => {
    const kind = dialog?.kind;
    setDialog(null);
    // Only the edit dialog refreshes the list; the two others just close (see migration.md).
    if (result === 'Confirm' && kind === 'edit')
      void queryClient.invalidateQueries({
        queryKey: productLinesQueryOptions().queryKey,
      });
  };

  if (!isAdmin) return null;

  return (
    <div className="container summary">
      <TopMenu />
      <div className="summary-table my-12">
        <button
          type="button"
          className="add-btn mb-4 flex items-center justify-center self-center"
          data-testid="header-add-product-line"
          onClick={() => setDialog({ kind: 'edit', item: null })}
        >
          <img src={plusIcon} alt="" />
          <span className="pl-2">Add a product line</span>
        </button>
        <div className="table-container">
          <div
            className="items-container"
            ref={containerRef}
            role="region"
            aria-label="Product lines"
          >
            <table className="table items">
              <thead>
                <tr>
                  {COLUMNS.map(({ key, label, width }) => (
                    <SortableHeader
                      key={key}
                      label={label}
                      width={width}
                      active={sort.by === key}
                      descending={sort.reverse}
                      onClick={() =>
                        setSort((current) => nextSort(current, key))
                      }
                      testId={`product-line-header-${kebabCase(key)}`}
                    />
                  ))}
                  <th style={{ width: '15%' }} className="edit-header">
                    actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const keyboards = keyboardLabel(item.keyboard);
                  const sav = item.sav ? 'YES' : 'NO';
                  const display =
                    item.display == null ? '' : String(item.display);
                  return (
                    <tr key={item.id} className="product-line-row">
                      <td
                        style={{ width: '10%' }}
                        title={item.productLine ?? ''}
                        data-testid="product-line-name"
                      >
                        {item.productLine}
                      </td>
                      <td
                        style={{ width: '15%' }}
                        title={item.commercialProductLine ?? ''}
                        data-testid="product-line-commercial"
                      >
                        {item.commercialProductLine}
                      </td>
                      <td
                        style={{ width: '5%' }}
                        title={display}
                        data-testid="product-line-display"
                      >
                        {display}
                      </td>
                      <td
                        style={{ width: '10%' }}
                        title={item.productGroup ?? ''}
                        data-testid="product-line-group"
                      >
                        {item.productGroup}
                      </td>
                      <td
                        style={{ width: '20%' }}
                        title={sav}
                        data-testid="product-line-personalization"
                      >
                        {sav}
                      </td>
                      <td
                        style={{ width: '15%' }}
                        title={keyboards}
                        data-testid="product-line-keyboards"
                      >
                        {keyboards}
                      </td>
                      <td style={{ width: '15%' }} className="edit-action">
                        <button
                          type="button"
                          className="button px-1"
                          onClick={() => setDialog({ kind: 'edit', item })}
                          data-testid="product-line-row-edit-button"
                        >
                          edit
                        </button>
                        <button
                          type="button"
                          className="button mx-1 px-1"
                          onClick={() => setDialog({ kind: 'xy', item })}
                          data-testid="product-line-row-xy-button"
                        >
                          ABC XY
                        </button>
                        <button
                          type="button"
                          className="button px-1"
                          onClick={() => setDialog({ kind: 'keyboards', item })}
                          data-testid="product-line-row-keyboards-button"
                        >
                          Keyboards
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {Array.from({ length: fillerRows }, (_, index) => (
                  <tr key={`empty-${index}`} className="empty-row">
                    {WIDTHS.map((width, cell) => (
                      <td key={cell} style={{ width: `${width}%` }} />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {dialog?.kind === 'edit' && (
        <ProductLineDialog
          key={dialog.item?.id ?? 'new'}
          item={dialog.item}
          stores={stores}
          languages={languages}
          onClose={closeDialog}
        />
      )}
      {dialog?.kind === 'xy' && (
        <ProductLineXyDialog
          key={dialog.item.id}
          item={dialog.item}
          onClose={closeDialog}
        />
      )}
      {dialog?.kind === 'keyboards' && (
        <KeyboardsDialog
          key={dialog.item.id}
          item={dialog.item}
          onClose={closeDialog}
        />
      )}
    </div>
  );
}
